"use client";

import { useEffect, useState, type ComponentProps } from "react";
import { Brain } from "lucide-react";
import Link from "next/link";
import { quizzes as quizDefinitions } from "~/config/quizzes";
import {
  filterQuizzesByCategory,
  type QuizCategoryLabel,
} from "~/config/quiz-categories";
import {
  Empty,
  EmptyDescription,
  EmptyHeader,
  EmptyTitle,
} from "~/components/ui/empty";
import { QuizCard } from "./quiz-card";
import {
  ContentCategoryNav,
  type ContentCategoryNavItem,
} from "~/components/patterns/content-category-nav";

interface QuizCategory {
  id: string;
  label: QuizCategoryLabel;
  description?: string;
}

type QuizCardData = Omit<ComponentProps<typeof QuizCard>["quiz"], "icon">;

interface FilterableQuizGridProps {
  quizzes: QuizCardData[];
  categories: readonly QuizCategory[];
  activeCategory: string;
}

export function FilterableQuizGrid({
  quizzes,
  categories,
  activeCategory,
}: FilterableQuizGridProps) {
  const [selectedCategory, setSelectedCategory] = useState(activeCategory);

  useEffect(() => {
    const syncCategoryFromUrl = () => {
      const category = new URLSearchParams(window.location.search).get(
        "kategorija",
      );
      setSelectedCategory(category ?? "all");
    };

    syncCategoryFromUrl();
    window.addEventListener("popstate", syncCategoryFromUrl);

    return () => window.removeEventListener("popstate", syncCategoryFromUrl);
  }, []);

  const filteredQuizzes = filterQuizzesByCategory(quizzes, selectedCategory);

  const categoryItems: ContentCategoryNavItem[] = [
    { id: "all", label: "Sve", href: "/testovi", count: quizzes.length },
    ...categories.map((category) => ({
      id: category.id,
      label: category.label,
      href: `/testovi?kategorija=${encodeURIComponent(category.id)}`,
      count: filterQuizzesByCategory(quizzes, category.id).length,
    })),
  ];

  return (
    <div className="space-y-8">
      <ContentCategoryNav
        items={categoryItems}
        selectedId={selectedCategory}
        ariaLabel="Filtriranje testova po kategorijama"
        onNavigate={(item) => setSelectedCategory(item.id)}
      />

      {filteredQuizzes.length ? (
        <section
          className="grid grid-cols-1 gap-x-6 gap-y-10 md:grid-cols-2 lg:grid-cols-3"
          aria-label="Lista psiholoških testova"
        >
          {filteredQuizzes.map((quiz) => (
            <QuizCard
              key={quiz.id}
              quiz={{
                ...quiz,
                icon:
                  quizDefinitions.find((item) => item.id === quiz.id)?.icon ??
                  Brain,
              }}
            />
          ))}
        </section>
      ) : (
        <Empty className="border-border border border-dashed">
          <EmptyHeader>
            <EmptyTitle>Nema testova u ovoj kategoriji</EmptyTitle>
            <EmptyDescription>
              Izaberi drugu kategoriju ili pogledaj sve testove.
            </EmptyDescription>
          </EmptyHeader>
          <Link
            href="/testovi"
            className="text-primary text-sm font-medium underline underline-offset-4"
          >
            Svi testovi
          </Link>
        </Empty>
      )}
    </div>
  );
}
